import config from 'config';
import axios from 'axios';
import FileSaver from 'file-saver';
import UserFlagsService from './user_flags_service';

const ROOT = config.API_BASE_URL;

class DevicesService {

  static getRoot() {
    return ROOT;
  }

  static getImage(deviceId, fileName, image) {
    return new Promise((resolve, reject) => {
      axios.get(`${ROOT}devices/${deviceId}/images/${fileName}`, image).then((data) => {
        resolve({ device: data.data, response: data });
      }).catch((err) => {
        // img does not exist
        reject(err);
      });
    });
  }

  static replaceImage(deviceId, file, imageId) {
    return new Promise((resolve, reject) => {
      const uploadData = new FormData();
      uploadData.append('file', file);

      axios.post(`${ROOT}devices/${deviceId}/images/replace/${imageId}`, uploadData).then((data) => {
        resolve({ deviceImage: data.data, response: data });
      }).catch((err) => {
        // img does not exist
        reject(err);
      });
    });
  }

  static deleteImage(deviceId, fileName, imageId) {
    return new Promise((resolve, reject) => {
      axios.delete(`${ROOT}devices/${deviceId}/images/${fileName}/${imageId}`).then((data) => {
        resolve({ device: data.data, response: data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static uploadImage(deviceId, file, fileName) {
    return new Promise((resolve, reject) => {
      const uploadData = new FormData();
      uploadData.append('file', file);

      axios.post(`${ROOT}devices/${deviceId}/images/`, uploadData).then((data) => {
        resolve({ deviceImage: data.data, response: data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getEventConfigurations(deviceId) {
    return new Promise((resolve, reject) => {
      axios.get(`${ROOT}devices/${deviceId}/alert-configurations`).then((data) => {
        resolve({ configurations: data.data, response: data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getDeviceMetadataHistory(deviceId, metadataId) {
    return new Promise((resolve, reject) => {
      axios.get(`${ROOT}devices/${deviceId}/metadata/${metadataId}/history`).then((data) => {
        resolve({ history: data.data, response: data });
      }).catch((err) => {
        reject(err);
      });
    });
  }  

  static updateMetadataForAMT(deviceId, amtId, amtMetadata) {
    return new Promise((resolve, reject) => {
      axios.patch(`${ROOT}devices/${deviceId}/amt/${amtId}`, amtMetadata).then((data) => {
        resolve({ device: data.data, response: data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static listAllForGroupingFetchIdAndNameOnly(groupingId) {
    return new Promise((resolve, reject) => {
      axios.get(`${ROOT}devices/list?filterByGroupingId=${groupingId}&idAndNameOnly=true${UserFlagsService.getUserFlagsAsURLParams(true)}`).then((data) => {
        resolve({ devices: data.data, response: data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static listAllForGrouping(groupingId, includeAlertState = false, minimal = false, summary = false) {
    return new Promise((resolve, reject) => {
      axios.get(`${ROOT}devices/list?filterByGroupingId=${groupingId}&includeAlertState=${includeAlertState}&minimal=${minimal}&summary=${summary}${UserFlagsService.getUserFlagsAsURLParams(true)}`).then((data) => {
        resolve({ devices: data.data, response: data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static listAllForGroupingAndDeviceDataClassification(options = []) {
    return new Promise((resolve, reject) => {
      const params = options.length == 0 ? '' : `?${options.map(option => Object.keys(option).map((k, i) => `${k}=${option[k]}`)).join('&')}`;
      axios.get(`${ROOT}devices/list-paged${params}`).then((data) => {
        resolve({ devices: data.data.content, response: data.data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getAll(options = [], minimal = false) {
    return new Promise((resolve, reject) => {
      const params = options.length == 0 ? '' : `?${options.map(option => Object.keys(option).map((k, i) => `${k}=${option[k]}`)).join('&')}`;

      axios.get(`${ROOT}devices${params}&minimal=${minimal}`).then((data) => {
        resolve({ devices: data.data.content, page: data.data, response: data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static listAll() {
    return new Promise((resolve, reject) => {
      axios.get(`${ROOT}devices/listAll`).then((data) => {
        resolve({ devices: data.data, response: data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getAllUnAssigned(options = []) {
    return new Promise((resolve, reject) => {
      const params = options.length == 0 ? '' : `?${options.map(option => Object.keys(option).map((k, i) => `${k}=${option[k]}`)).join('&')}`;
      axios.get(`${ROOT}devices/listUnAssigned${params}`).then((data) => {
        resolve({ devices: data.data.content, page: data.data, response: data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getCountsByRate(deviceGroupId) {
    return new Promise((resolve, reject) => {
      axios.get(`${ROOT}devices/count-by-rate?deviceGroupId=${deviceGroupId}`).then((response) => {
        resolve({ result: response.data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getForRate(sampleRate, deviceGroupId) {
    return new Promise((resolve, reject) => {
      axios.get(`${ROOT}devices/rate-of/${sampleRate}?deviceGroupId=${deviceGroupId}`).then((response) => {
        resolve({ devices: response.data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static get(deviceId, includeAlertState = false) {
    return new Promise((resolve, reject) => {
      axios.get(`${ROOT}devices/${deviceId}?includeAlertState=${includeAlertState}`).then((data) => {
        resolve({ device: data.data, response: data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getBySerialNumber(deviceSerialNumber) {
    return new Promise((resolve, reject) => {
      axios.get(`${ROOT}devices/sn/${deviceSerialNumber}`).then((data) => {
        resolve({ device: data.data, response: data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getAssociatedAssetMeasures(deviceId) {
    return new Promise((resolve, reject) => {
      axios.get(`${ROOT}devices/${deviceId}/asset-measures`).then((data) => {
        resolve({ assetMeasures: data.data, response: data });
      }).catch((err) => {
        reject(err);
      });
    });
  }


  static getSubDevices(deviceSerialNumber) {
    return new Promise((resolve, reject) => {
      axios.get(`${ROOT}devices/${deviceSerialNumber}/sub-devices`).then((data) => {
        resolve({ subDevices: data.data, response: data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static updateConfiguration(deviceId, updateData) {
    return new Promise((resolve, reject) => {
      axios.patch(`${ROOT}devices/${deviceId}/configuration`, updateData).then((data) => {
        resolve({ device: data.data.device, metadata: data.data.metadata, response: data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static bulkUpdateConfiguration(updateData) {
    return new Promise((resolve, reject) => {
      axios.patch(`${ROOT}devices/configurations/bulk`, updateData).then((data) => {
        resolve({ devices: data.data.devices, metadata: data.data.metadata, response: data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getDeviceAlertLevelThresholds(deviceId, deviceDataTypeId = null) {
    return new Promise((resolve, reject) => {
      const query = deviceDataTypeId === null ? '' : `?deviceDataTypeId=${deviceDataTypeId}`;
      axios.get(`${ROOT}devices/${deviceId}/device-alert-level-threshold${query}`).then((data) => {
        resolve({ alertLevelThresholds: data.data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getDeviceReceivedDataByDeviceDataType(timeConstraint, endAtDate, deviceId, deviceDataTypeId) {
    return new Promise((resolve, reject) => {
      const query = `deviceId=${deviceId}&deviceDataTypeId=${deviceDataTypeId}&timeConstraint=${timeConstraint}&endAtDate=${endAtDate}${UserFlagsService.getUserFlagsAsURLParams(true)}`;
      axios.get(`${ROOT}reports/device-readings-report?${query}`).then((data) => {
        resolve({ readingsData: data.data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getDeviceDetailReportCSV(startAtDate, endAtDate, deviceId) {
    return new Promise((resolve, reject) => {
      const query = `deviceId=${deviceId}&startAtDate=${startAtDate}&endAtDate=${endAtDate}${UserFlagsService.getUserFlagsAsURLParams(true)}`;
      axios.post(`${ROOT}reports/detail-csv-report?${query}`).then((response) => {
        const blob = new Blob([response.data], { type: 'application/csv' });
        FileSaver.saveAs(blob, 'report.csv');
        resolve();
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getDeviceDetailReport(startAtDate, endAtDate, deviceId) {
    return new Promise((resolve, reject) => {
      const query = `deviceId=${deviceId}&startAtDate=${startAtDate}&endAtDate=${endAtDate}${UserFlagsService.getUserFlagsAsURLParams(true)}`;
      axios.get(`${ROOT}reports/device-detail-report?${query}`).then((data) => {
        resolve({ detailReportData: data.data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getAllDeviceReport(startAtDate, endAtDate, groupingId) {
    return new Promise((resolve, reject) => {
      const query = `groupingId=${groupingId}&startAtDate=${startAtDate}&endAtDate=${endAtDate}${UserFlagsService.getUserFlagsAsURLParams(true)}`;
      axios.get(`${ROOT}reports/all-devices-report?${query}`).then((data) => {
        resolve({ allDeviceReport: data.data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getDeviceComparisonReport(startAtDate, endAtDate, deviceIds, deviceDataTypeId) {
    return new Promise((resolve, reject) => {
      const query = `startAtDate=${startAtDate}&endAtDate=${endAtDate}&deviceIds=${deviceIds}&deviceDataTypeId=${deviceDataTypeId}${UserFlagsService.getUserFlagsAsURLParams(true)}`;
      axios.get(`${ROOT}reports/device-comparison-report?${query}`).then((data) => {
        resolve({ report: data.data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static findUnregisteredBySerialNumber() {
    return new Promise((resolve, reject) => {
      axios.get(`${ROOT}devices/findUnregisteredBySerialNumber`).then((data) => {
        resolve({ devices: data.data, response: data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getGatewayFromMacAddress(macAddress) {
    return new Promise((resolve, reject) => {
      const query = `macAddress=${macAddress}`;
      axios.get(`${ROOT}devices/findByMacAddress?${query}`).then((data) => {
        resolve({ device: data.data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getTotalEventCount(deviceId) {
    return new Promise((resolve, reject) => {
      axios.get(`${ROOT}devices/${deviceId}/events`).then((data) => {
        resolve({ eventCount: data.data });
      }).catch((err) => {
        reject(err);
      });
    });
  }


  static deleteAllEventsForDevice(deviceId) {
    return new Promise((resolve, reject) => {
      axios.delete(`${ROOT}devices/${deviceId}/events`).then(() => {
        resolve();
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getHologramDeviceInfo(imei) {
    return new Promise((resolve, reject) => {
      axios.get(`${ROOT}cellular/hologram/devices?imei=${imei}`).then((response) => {
        resolve(response.data);
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getDeviceTypes(serialNumberTypeToken) {
    return new Promise((resolve, reject) => {
      const params = serialNumberTypeToken ? `?serialNumberTypeToken=${serialNumberTypeToken}` : '';
      axios.get(`${ROOT}devices/types${params}`).then((response) => {
        resolve({ deviceTypes: response.data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

  static getAvailableControllers() {
    return new Promise((resolve, reject) => {
      axios.get(`${ROOT}devices/listControllers`).then((response) => {
        resolve({ availableControllers: response.data });
      }).catch((err) => {
        reject(err);
      });
    });
  }

}

export default DevicesService;
